<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_slug.name',
	description: '$t:blocks.input_slug.description',
	category: 'input',
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			const types = ['slug'];
			if (isPrimitiveTypeString(primitiveType)) types.push('identifier');
			return types;
		},
	},
	options: [
		{ name: 'modelValue', label: '$t:blocks.common.props.value', model: true },
		{ name: 'sourceField', label: '$t:blocks.input_slug.props.source_field', block: BuiltinBlockIds.SystemCollectionFieldSelect },
		{ name: 'placeholder', label: '$t:blocks.common.props.placeholder' },
		{ name: 'variant', label: '$t:blocks.common.props.variant', type: 'enum', values: BLOCK_INPUT_VARIANT_OPTIONS },
		{ name: 'size', label: '$t:blocks.common.props.size', type: 'enum', values: BLOCK_CONTROL_SIZE_OPTIONS },
		{ name: 'leadingIcon', label: '$t:blocks.common.props.leading_icon', block: BuiltinBlockIds.InputIcon, nullable: true },
		{ name: 'trailingIcon', label: '$t:blocks.common.props.trailing_icon', block: BuiltinBlockIds.InputIcon, nullable: true },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'loading', label: '$t:blocks.common.props.loading' },
		{ name: 'block', label: '$t:blocks.common.props.block' },
		{ name: 'autocomplete', label: '$t:blocks.common.props.autocomplete' },
		{ name: 'modelModifiers', hidden: true },
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { GlobalPrimitiveFieldId } from '#layers/data-model/public';
import type { InputProps } from '#layers/ui/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed, ref, watch } from 'vue';
import { slugify } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { DisplayTemplateConfigurationError, isPrimitiveTypeString } from '#layers/data-model/public';
import { injectFormBlockContext, useFormFieldValue, usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends InputBlockProps, Pick<
	InputProps<any>,
	'placeholder' | 'variant' | 'size' | 'leadingIcon' | 'trailingIcon' | 'loading' | 'autocomplete' | 'modelModifiers'
> {
	block?: boolean;
	allowUpdate?: boolean;
	/** Source field to generate the slug from (from slug field type options). */
	sourceField?: GlobalPrimitiveFieldId | null;
}

const {
	allowUpdate = true,
	sourceField,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	placeholder,
	variant,
	size,
	leadingIcon,
	trailingIcon,
	loading,
} = defineProps<Props>();
const model = defineModel<string>();

;

const forwarded = computed(() => ({ placeholder, variant, size, leadingIcon, trailingIcon, loading }));

const formBlockContext = injectFormBlockContext(null);
const { value: sourceValue, isMissing: sourceFieldMissing, displayName: sourceFieldDisplayName } = useFormFieldValue(formBlockContext, () => sourceField);
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const isCreate = computed(() => formBlockContext?.isCreate.value ?? false);
const allowUpdateInEditMode = ref(isCreate.value);
const hasValue = computed(() => !!modelValue.value);

const inputDisabled = computed(
	() => disabled.value || (!allowUpdate && (hasValue.value && !isCreate.value)),
);

const isDirty = ref(false);

const slugModel = computed({
	get: () => modelValue.value,
	set: (value: string | undefined) => {
		const formatted = slugify(value, { trim: false }) ?? '';
		modelValue.value = formatted;

		// Once the user has manually edited the slug, stop auto-syncing from the source field
		// If the user has manually cleared the slug and the source field has a value, set isDirty to true
		isDirty.value = formatted.length > 0 || value !== sourceValue.value;
	},
});

function generateSlug() {
	allowUpdateInEditMode.value = true;

	const slug = slugify(String(sourceValue.value)) ?? '';
	modelValue.value = slug;

	isDirty.value = false;
}

watch(isCreate, (value) => {
	allowUpdateInEditMode.value = value;
});

watch(sourceValue, (value) => {
	if (!allowUpdateInEditMode.value) {
		return;
	}

	// If the source field becomes empty, clear the slug if it wasn't manually edited
	if (value == null || value === '') {
		if (!isDirty.value) {
			modelValue.value = '';
		}

		return;
	}

	if (sourceFieldMissing.value) return;

	// On edit: don't auto-update if user has manually edited the slug
	if (isDirty.value) {
		return;
	}

	const slug = slugify(String(value)) ?? '';
	if (slug) modelValue.value = slug;
}, { immediate: true });
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInput
			v-bind="forwarded"
			v-model="slugModel"
			:model-modifiers="modelModifiers ?? {}"
			:disabled="inputDisabled"
			:readonly
			:aria-readonly="readonly"
			class="min-w-0 flex-1 font-mono font-medium"
		/>

		<DisplayTemplateConfigurationError
			v-if="sourceFieldMissing"
			:error="{ message: $t('blocks.input_slug.warning.source_field_missing') }"
		/>

		<div v-else-if="sourceFieldDisplayName" class="flex items-center text-xs text-muted">
			<template v-if="!isDirty && allowUpdateInEditMode">
				<DIcon name="lucide:link" class="me-1" />
				<i18n-t keypath="blocks.input_slug.hint">
					<template #sourceFieldDisplayName>
						<span class="ms-0.5 font-semibold">
							{{ sourceFieldDisplayName }}
						</span>
					</template>
				</i18n-t>
			</template>

			<template v-else>
				<button v-if="!readonly" class="flex items-center" type="button" @click="generateSlug()">
					<DIcon name="lucide:refresh-cw" class="me-1" />
					<i18n-t keypath="blocks.input_slug.suggestion">
						<template #sourceFieldDisplayName>
							<span class="ms-0.5 font-semibold">
								{{ sourceFieldDisplayName }}
							</span>
						</template>
					</i18n-t>
				</button>
			</template>
		</div>
	</DTooltip>
</template>
